import React, { useEffect, useRef, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  LabelList,
  Label,
} from "recharts";
import { Box, Grid, IconButton, Typography, Button,Snackbar,Alert } from "@mui/material";
import { DARKCOLORS, LIGHTCOLORS } from "../../../Utils/constants";
import { useTranslation } from "react-i18next";
import { useThemeContext } from "../../../Context/ThemeContext";
import { getSalesByLocation } from "../../../Services/authService";
import SectionLoader from "../../../Components/Common/SectionLoader";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import "../../../Styles/daterange-responsive.css";
import { DateRange } from "react-date-range";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import ExportIcon from "../../../assets/Export_icon.svg";

const SalesByLocation = () => {
  const { t } = useTranslation();
  const { mode } = useThemeContext();
  const calendarRef = useRef(null);
  const calendarButtonRef = useRef(null);

  const textColor =
    mode === "dark"
      ? DARKCOLORS.BodyLightBackground
      : LIGHTCOLORS.BodyLightBackground;
  const borderColor2 =
    mode === "dark" ? DARKCOLORS.GreyScale30 : LIGHTCOLORS.GreyScale20;
  const barColors =
    mode === "dark" ? DARKCOLORS.Secondary500 : LIGHTCOLORS.Secondary500;
  const containerBackground =
    mode === "dark" ? DARKCOLORS.BackgroundCard : LIGHTCOLORS.BackgroundCard;
  const dropIcon =
    mode === "dark" ? DARKCOLORS.GreyScale80 : LIGHTCOLORS.GreyScale80;
  const btnColor1 =
    mode === "dark" ? DARKCOLORS.Primary500 : LIGHTCOLORS.Primary500;
  const btnColor =
    mode === "dark" ? DARKCOLORS.GreyScale10 : LIGHTCOLORS.GreyScale10;
  const colorSuccessExport =
    mode === "dark" ? DARKCOLORS.Secondary500 : LIGHTCOLORS.Secondary500;
  const settingIconColor =
    mode === "dark" ? DARKCOLORS.BaseBlack : LIGHTCOLORS.BaseBlack;
  const reset =
    mode === "dark" ? DARKCOLORS.Primary500 : LIGHTCOLORS.Primary500;
  const scroll1 =
    mode === "light"
      ? LIGHTCOLORS.BodyDarkBackground
      : DARKCOLORS.BodyLightBackground;

  const scroll =
    mode === "light" ? LIGHTCOLORS.GreyScale40 : DARKCOLORS.GreyScale40;

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState([{ key: "selection" }]);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [calendarPosition, setCalendarPosition] = useState({ top: 0, left: 0 });

  const [open1, setOpen1] = useState(false);
const [toastMessage, setToastMessage] = useState("");

  const handleDateChange = (item) => {
    setDateRange([item.selection]);
  };

  const handleCalendarToggle = () => {
    if (calendarButtonRef.current) {
      const rect = calendarButtonRef.current.getBoundingClientRect();
      setCalendarPosition({
        top: rect.bottom + window.scrollY + 10,
        left: rect.left + window.scrollX,
      });
    }
    setIsCalendarOpen((prev) => !prev);
  };

  const handleSnackbarClose = (event, reason) => {
  if (reason === "clickaway") {
    return;
  }

  setOpen1(false);
};

 const handleExport = () => {
  if (!data || data.length === 0) {
    setToastMessage(t("sales_analytics.noDataToExport"));
    setOpen1(true);
    return;
  }

  try {
    const rows = data.map((item) => ({
      [t("sales_analytics.location")]: item.location || "",
      [t("sales_analytics.machine_number")]: item.machineNumber || "",
      [t("sales_analytics.city")]: item.city || "",
      [t("sales_analytics.total_sales")]: item.totalSales ?? 0,
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(wb, ws, "Sales By Location");

    XLSX.writeFile(
  wb,
  `${t("sales_analytics.salesByLocationFileName")}_${new Date()
    .toISOString()
    .slice(0, 10)}.xlsx`
);
  } catch (error) {
    console.error("Error exporting sales by location:", error);

    setToastMessage(
      t("dashboard.exportFailed", "Error exporting data")
    );
    setOpen1(true);
  }
};

  const [hasDateSelected, setHasDateSelected] = useState(false);

  const fetchData = async (applyDate = false) => {
    setLoading(true);
    setError(null);

    try {
      let startDate = "";
      let endDate = "";

      if (applyDate && dateRange[0]?.startDate && dateRange[0]?.endDate) {
        startDate = dayjs(dateRange[0].startDate)
          .startOf("day")
          .format("YYYY-MM-DDTHH:mm:ss");

        endDate = dayjs(dateRange[0].endDate)
          .endOf("day")
          .format("YYYY-MM-DDTHH:mm:ss");
      }

      const salesData = await getSalesByLocation(startDate, endDate);
      setData(salesData);
    } catch (err) {
      setError("Failed to load sales data.");
    } finally {
      setLoading(false);
    }
  };

  // Initial load — no dates
  useEffect(() => {
    fetchData(false);
  }, []);

  // Apply button — send selected dates
  const handleApply = () => {
    setHasDateSelected(true);
    fetchData(true);
    setIsCalendarOpen(false);
  };

  // Reset button — clear date range and reload all data
  const handleReset = () => {
    setDateRange([{ key: "selection" }]);
    setHasDateSelected(false);
    fetchData(false);
    setIsCalendarOpen(false);
  };

  // Close the calendar when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(event.target) &&
        calendarButtonRef.current &&
        !calendarButtonRef.current.contains(event.target)
      ) {
        setIsCalendarOpen(false);
      }
    }

    if (isCalendarOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isCalendarOpen]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Box sx={{ background: "white", padding: "10px", borderRadius: "5px" }}>
          <p style={{ margin: 0, color: "#666" }}>{label}</p>
          <p style={{ margin: 0, fontWeight: "bold", color: "#32CD32" }}>
            sales: € {payload[0].value}
          </p>
        </Box>
      );
    }
    return null;
  };

  // Custom tick component for multi-line X-axis labels
  // const CustomXAxisTick = ({ x, y, payload, color }) => {
  //   const item = data[payload.index];

  //   return (
  //     <text style={{ maxWidth: '80px' }} className="x-location-div" x={x} y={y} textAnchor="middle" fill={color} fontSize={12}>
  //       <tspan className="x-location-text" x={x} dy="8">{item.location}</tspan>
  //       <tspan className="x-location-text" x={x} dy="18">{item.machineNumber}</tspan>
  //       <tspan className="x-location-text" x={x} dy="18">{item.city}</tspan>
  //     </text>
  //   );
  // };

  const CustomXAxisTick = ({ x, y, payload }) => {
    const item = data[payload.index];
    const textColor =
      mode === "dark"
        ? DARKCOLORS.BodyLightBackground
        : LIGHTCOLORS.BodyLightBackground;

    const truncate = (text, max = 15) =>
      text?.length > max ? text.slice(0, max) + "..." : text;

    const location = truncate(item?.location || "");
    const machineNumber = item?.machineNumber || "";
    const city = item?.city || "";

    const tooltipText = `${item?.location || ""}\n${machineNumber}\n${city}`;

    return (
      <g transform={`translate(${x - 10},${y + 5}) rotate(-45)`}>
        <title>{tooltipText}</title>

        <text
          x={0}
          y={0}
          textAnchor="end"
          fill={textColor}
          fontSize={11}
          style={{ pointerEvents: "all" }}
        >
          <tspan x={0} dy="1.2em">
            {machineNumber}
          </tspan>

          <tspan x={0} dy="1.2em">
            {city}
          </tspan>
        </text>
      </g>
    );
  };

  return (
    <Box>
      <Grid
        container
        //justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2, gap: "1px" }}
      >
        <h3
          className="subheading-sec-card"
          style={{ margin: "0px", marginRight: "auto" }}
        >
          {t("sales_analytics.salesbyLocation")}
        </h3>
        {/* Calendar Icon + current range */}
        <Button
          className="sale-calender"
          ref={calendarButtonRef}
          aria-label="select-date"
          onClick={handleCalendarToggle}
          sx={{
            background: btnColor,
            borderRadius: "50px",
            padding: "8px 14px",
            color: dropIcon,
            textTransform: "none",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <Typography
            sx={{ fontSize: "13px", color: textColor, fontWeight: 500 }}
          >
            {dateRange[0]?.startDate && dateRange[0]?.endDate
              ? `${dayjs(dateRange[0].startDate).format("DD MMM YYYY")} - ${dayjs(dateRange[0].endDate).format("DD MMM YYYY")}`
              : t("sales_analytics.select_date_range", "Select Date Range")}
          </Typography>
          <CalendarTodayOutlinedIcon style={{ fontSize: "1rem" }} />
        </Button>
        <Box
          onClick={handleExport}
          sx={{
            background: colorSuccessExport,
            color: settingIconColor,
            padding: "6px",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "24px",
            height: "24px",
            flexShrink: 0,
            cursor: "pointer",
            "&:hover": {
              opacity: 0.8,
            },
          }}
          title={t("dashboard.export", "Export")}
        >
          <img
            src={ExportIcon}
            alt="Export"
            style={{
              width: "12px",
              height: "12px",
            }}
          />
        </Box>

        {/* Date Range Picker */}
        {isCalendarOpen && (
          <div
            className="sales-calender"
            ref={calendarRef}
            style={{
              background: btnColor,
              position: "absolute",
              top: `${calendarPosition.top}px`,
              right: "25px",
              zIndex: 1000,
              display: "flex",
              flexDirection: "column",
              borderRadius: "12px",
              border: `1px solid ${borderColor2}`,
            }}
          >
            <DateRange
              editableDateInputs={true}
              onChange={handleDateChange}
              moveRangeOnFirstSelection={false}
              ranges={dateRange}
              style={{
                borderRadius: "12px",
                padding: "8px",
              }}
            />
            <div
              style={{
                textAlign: "right",
                margin: "1rem 0.5rem",
                display: "flex",
                gap: "0.5rem",
                justifyContent: "flex-end",
              }}
            >
              {/* className="cancel-btn" */}
              <button
                className="cancel-btn"
                type="button"
                style={{
                  color: reset,
                  borderColor: reset,
                  padding: "10px 15px",
                  fontSize: "15px",
                }}
                onClick={handleReset}
              >
                {t("sales_analytics.reset", "Reset")}
              </button>
              <button
                type="button"
                style={{
                  background: btnColor1,
                  padding: "10px 15px",
                  fontSize: "15px",
                }}
                className="save-btn"
                onClick={handleApply}
              >
                {t("sales_analytics.apply")}
              </button>
            </div>
          </div>
        )}
      </Grid>

      <Box
        sx={{
          overflowX: "auto",
          overflowY: "hidden",
          // '&::-webkit-scrollbar': {
          //   width: '10px !important',
          //   height: '8px !important',
          //   padding: '2px !important',
          // },
          // '&::-webkit-scrollbar-track': {
          //   backgroundColor: scroll1,
          //   borderRadius: '10px',
          //   border: `1px solid ${scroll}`,
          // },
          // '&::-webkit-scrollbar-thumb': {
          //   backgroundColor: scroll,
          //   borderRadius: '10px',
          //   width: '3px !important',

          //   border: '3px solid transparent',
          //   backgroundClip: 'padding-box',
          //   '&:hover': {
          //     backgroundColor: scroll,
          //   },
          // },
        }}
      >
        <Box
          sx={{
            width: "100%",
            "@media (max-width: 1400px)": {
              minWidth: "1500px",
            },
          }}
        >
          {loading ? (
            <SectionLoader />
          ) : error ? (
            <Typography color="error">{error}</Typography>
          ) : data.length === 0 ? (
            <Box
              sx={{
                height: "600px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
              }}
            >
              <Typography
                sx={{
                  color: textColor,
                  fontSize: "14px",
                  fontWeight: 500,
                }}
              >
                {t("sales_analytics.noSalesByLocationData")}
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height={600}>
              <BarChart
                data={data}
                margin={{ top: 30, right: 10, left: 0, bottom: 60 }}
              >
                <CartesianGrid
                  horizontal={true}
                  vertical={false}
                  stroke={borderColor2}
                />
                <XAxis
                  dataKey="location"
                  tick={<CustomXAxisTick color={textColor} />}
                  interval={0}
                  height={70}
                >
                  <Label
                    value={t("sales_analytics.location")}
                    position="insideBottom"
                    offset={-55}
                    fill={textColor}
                    fontSize={12}
                  />
                </XAxis>
                <YAxis
                  className="y-sales-number"
                  domain={[0, "auto"]}
                  width={70}
                  tickFormatter={(value) => `${value}`}
                  tick={{ fill: textColor }}
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                >
                  <Label
                    value="€"
                    position="insideLeft"
                    offset={10}
                    fill={textColor}
                    fontSize={13}
                  />
                </YAxis>
                {/* <Tooltip content={<CustomTooltip />} cursor={{ fill: "transparent" }} /> */}
                <Bar
                  dataKey="totalSales"
                  fill={barColors}
                  barSize={64}
                  radius={6}
                >
                  <LabelList
                    dataKey="totalSales"
                    position="top"
                    fill={textColor}
                    fontSize={12}
                    formatter={(value) => {
                      if (Number(value) > 0) {
                        return `€ ${value}`;
                      }
                      return null;
                    }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Box>
      </Box>
      <Snackbar
  open={open1}
  autoHideDuration={4000}
  onClose={handleSnackbarClose}
  anchorOrigin={{
    vertical: "top",
    horizontal: "center",
  }}
>
  <Alert
    onClose={handleSnackbarClose}
    severity="error"
    sx={{ width: "100%" }}
  >
    {toastMessage}
  </Alert>
</Snackbar>
    </Box>
  );
};

export default SalesByLocation;
